import {measureElement, observeElementRect, useVirtualizer} from '@tanstack/react-virtual'
import useStableCallback from 'common/hooks/useStableCallback'
import {ArrowRight, Check, FileCheck2, Settings, Sparkles, X} from 'lucide-react'
import React, {
  FC,
  ReactNode,
  RefObject,
  memo,
  useEffect,
  useMemo,
  useReducer,
  useRef,
  useState,
} from 'react'
import {useIntl} from 'react-intl'

import type {LessonPlanningCell, LessonPlanningData} from '../../types/teacherAllocationAi'
import {
  type AllocationCounts,
  type AllocationRow,
  type AllocationStatusFilter,
  type ProposalChange,
  branchValues,
  buildAllocationMatrix,
  cellMatchesStatus,
  filterChoices,
  groupRowsByBranch,
  levelValues,
  parseDuration,
  parseFrequency,
  rowsInScope,
  slotKeyFor,
} from '../../utils/teacherAllocationModel'
import {AllocationFilterSelect, AllocationProgressBar} from './AllocationShared'
import {GridSkeleton} from './AllocationSkeleton'
import AllocationTeacherSelect, {type AllocationTeacherOption} from './AllocationTeacherSelect'

export type AllocationGridPreview = {
  changes: Map<string, ProposalChange[]>
  count: number
  isApplying: boolean
  onApply: () => void
  onBackToConversation: () => void
  onDiscard: () => void
  /** Server flow: changes in cells the current Branch / Level hides. */
  outside?: number
}

/**
 * Server flow: cells a Versi proposal just changed (`applied`, green) or would have changed
 * (`discarded`, red) flash once — the earlier grid's highlighter. `key` restarts the flash.
 */
export type AllocationGridFlash = {
  slots: ReadonlySet<string>
  tone: 'applied' | 'discarded'
  key: number
}

/**
 * Server flow: the Branch / Level dropdowns as the server resolved them (ids as values). The
 * rows passed in are already that scope's, so they are not narrowed again here.
 */
export type AllocationGridScopeOptions = {
  branches: {values: string[]; hasAll: boolean}
  levels: {values: string[]; hasAll: boolean}
  branchLabel: (branch: string) => string
  /** A header per branch (Branch = All in a multi-branch school). */
  groupByBranch: boolean
}

type Props = {
  rows: AllocationRow[]
  grid: LessonPlanningData
  deleted: ReadonlySet<string>
  teachers: AllocationTeacherOption[]
  importedSlots: ReadonlySet<string>
  counts: AllocationCounts
  /** Imported assignments exist: upload-related intro copy and the file-icon note. */
  hasImports: boolean
  branch: string
  level: string
  onBranchChange: (branch: string) => void
  onLevelChange: (level: string) => void
  levelLabel: (level: string) => string
  defaultFrequency: string
  /** The cell whose settings panel is open (highlighted). */
  activeSlotKey: string | null
  /** Versi's pending proposal, previewed read-only on the grid. */
  preview: AllocationGridPreview | null
  onSelectTeacher: (
    row: AllocationRow,
    subject: string,
    teacher: AllocationTeacherOption | null
  ) => void
  onOpenSettings: (row: AllocationRow, subject: string) => void
  /** Column removal follows the status filter the grid shows (handover, 3C). */
  onRemoveColumn: (subject: string, filter: AllocationStatusFilter) => void
  onRemoveRow: (row: AllocationRow) => void
  onOpenAi: () => void
  footer: ReactNode
  scopeOptions?: AllocationGridScopeOptions
  /** Server flow: the rows are being fetched (first load, a new Branch / Level): a skeleton. */
  loading?: boolean
  /** Server flow: the same view is read again after a change: the table stays, a shimmer runs. */
  refreshing?: boolean
  /**
   * Server flow: pending changes are drawn as in the earlier grid — a blinking yellow highlight,
   * jiggling content, the incoming teacher's name blinking in over the current one.
   */
  changeAnimations?: boolean
  flash?: AllocationGridFlash | null
}

const STATUS_FILTERS: AllocationStatusFilter[] = ['all', 'assigned', 'unassigned']

type Tip = {subject: string; section: string; branch: string; x: number; y: number}

type RowProps = {
  row: AllocationRow
  columns: string[]
  /** This section's cells — the same object while none of them changes. */
  cells: Record<string, LessonPlanningCell> | undefined
  /** This section's not-taught / imported subjects, joined: cheap to compare for memo. */
  notTaught: string
  imported: string
  /** Server flow: this section's flashing subjects (joined), their tone and the flash's key. */
  flashing?: string
  flashTone?: AllocationGridFlash['tone']
  flashKey?: number
  changeAnimations?: boolean
  statusFilter: AllocationStatusFilter
  activeSubject: string | null
  preview: AllocationGridPreview | null
  teachers: AllocationTeacherOption[]
  /** Section name for labels (with the branch when two branches are shown). */
  section: string
  defaultFrequency: string
  onSelect: (row: AllocationRow, subject: string, teacher: AllocationTeacherOption | null) => void
  onSettings: (row: AllocationRow, subject: string) => void
  onRemoveRow: (row: AllocationRow) => void
  onTip: (tip: Tip | null) => void
  dataIndex: number
  measureRef: (el: Element | null) => void
}

/**
 * One section row. Memoized: editing a cell re-renders only its own row, and a filter change
 * re-renders rows without rebuilding anything in the step around the grid.
 */
const GridRow: FC<RowProps> = memo(
  ({
    row,
    columns,
    cells,
    notTaught,
    imported,
    flashing,
    flashTone,
    flashKey,
    changeAnimations,
    statusFilter,
    activeSubject,
    preview,
    teachers,
    section,
    defaultFrequency,
    onSelect,
    onSettings,
    onRemoveRow,
    onTip,
    dataIndex,
    measureRef,
  }) => {
    const intl = useIntl()
    const t = (id: string, values?: Record<string, ReactNode>) =>
      intl.formatMessage({id: `ONBOARDING_WIZARD.TEACHER_ALLOCATION.${id}`}, values as any)
    const readOnly = Boolean(preview)
    const notTaughtSet = notTaught ? new Set(notTaught.split('\n')) : null
    const importedSet = imported ? new Set(imported.split('\n')) : null
    const flashingSet = flashing ? new Set(flashing.split('\n')) : null

    const describeValue = (change: ProposalChange, value: string, weekly?: number | null) => {
      if (change.kind === 'frequency') {
        const {count, per} = parseFrequency(value, defaultFrequency)
        if (per === 'day' && weekly) return t('AI.PER_DAY_WEEKLY_VALUE', {count, weekly})
        return t(per === 'day' ? 'AI.PER_DAY_VALUE' : 'AI.PER_WEEK_VALUE', {count})
      }
      if (change.kind === 'duration') return t('AI.PERIODS_VALUE', {count: parseDuration(value)})
      if (change.kind === 'remove' && value === '') return t('GRID.NOT_TAUGHT')
      return value || t('AI.UNASSIGNED')
    }

    const renderCell = (subject: string) => {
      if (!row.subjects.includes(subject)) {
        return <td key={subject} className='ta-grid__cell ta-grid__cell--na' />
      }
      const cell = cells?.[subject]
      const state = notTaughtSet?.has(subject)
        ? 'notTaught'
        : String(cell?.teacher ?? '').trim()
          ? 'assigned'
          : 'unassigned'
      const pendingChanges = preview?.changes.get(slotKeyFor(row, subject))
      // Not in the selected tab: an empty cell (alignment kept), so assigning one on Unassigned
      // takes it off that tab at once, and clearing one on Assigned moves it to Unassigned.
      if (!pendingChanges && !cellMatchesStatus(state, statusFilter)) {
        return <td key={subject} className='ta-grid__cell ta-grid__cell--filtered' />
      }
      const flashes = Boolean(flashingSet?.has(subject)) && !pendingChanges
      // A new flash remounts the cell, so its animation runs again.
      const cellKey = flashes ? `${subject}#${flashKey}` : subject
      const classes = [
        'ta-grid__cell',
        activeSubject === subject ? 'is-active' : '',
        pendingChanges ? 'is-pending' : '',
        pendingChanges && changeAnimations ? 'ta-grid__cell--ai-pending' : '',
        flashes ? `ta-grid__cell--ai-${flashTone}` : '',
        state === 'notTaught' ? 'ta-grid__cell--not-taught' : '',
      ]
        .filter(Boolean)
        .join(' ')

      if (pendingChanges && changeAnimations) {
        // As the earlier grid drew a pending AI change: an assignment or replacement shows the
        // cell's own teacher field, whose name fades out while the incoming one blinks in; any
        // other change shows its current → proposed line, jiggling.
        const incoming = pendingChanges.find((c) => c.kind === 'new' || c.kind === 'replace')
        const others = pendingChanges.filter((c) => c !== incoming)
        // Periods only: the teacher stays, so it is shown (and inspectable) as on a normal cell.
        const settingsOnly = pendingChanges.every(
          (c) => c.kind === 'frequency' || c.kind === 'duration'
        )
        return (
          <td key={cellKey} className={classes}>
            {settingsOnly && (
              <div className='ta-grid__cell-inner'>
                <AllocationTeacherSelect
                  teachers={teachers}
                  value={String(cell?.teacher ?? '')}
                  selectedEmployeeId={String(cell?.teacherEmployeeId ?? '')}
                  disabled
                  ariaLabel={t('GRID.TEACHER_ARIA', {subject, section})}
                  onSelect={() => undefined}
                />
              </div>
            )}
            {incoming && (
              <div className='ta-grid__cell-inner ta-ai-incoming'>
                <div className='ta-ai-incoming__field'>
                  <AllocationTeacherSelect
                    teachers={teachers}
                    value={String(cell?.teacher ?? '')}
                    selectedEmployeeId={String(cell?.teacherEmployeeId ?? '')}
                    disabled
                    ariaLabel={t('GRID.TEACHER_ARIA', {subject, section})}
                    onSelect={() => undefined}
                  />
                  <span className='ta-ai-incoming__name'>
                    <span>{describeValue(incoming, incoming.proposed)}</span>
                  </span>
                </div>
              </div>
            )}
            {others.length > 0 && (
              <div className='ta-grid__pending ta-ai-jiggle'>
                {others.map((change) => (
                  <div key={change.kind} className='ta-grid__pending-line'>
                    <span className='ta-grid__pending-current'>
                      {/* A removal's current value is its teacher, or none. */}
                      {change.kind === 'remove'
                        ? change.current || t('AI.UNASSIGNED')
                        : describeValue(change, change.current, change.currentWeekly)}
                    </span>
                    <ArrowRight size={13} aria-hidden />
                    <span className='ta-grid__pending-proposed'>
                      {describeValue(change, change.proposed, change.proposedWeekly)}
                    </span>
                  </div>
                ))}
              </div>
            )}
          </td>
        )
      }

      if (pendingChanges) {
        return (
          <td key={cellKey} className={classes}>
            <div className='ta-grid__pending'>
              {pendingChanges.map((change) => (
                <div key={change.kind} className='ta-grid__pending-line'>
                  <span className='ta-grid__pending-current'>
                    {describeValue(change, change.current, change.currentWeekly)}
                  </span>
                  <ArrowRight size={13} aria-hidden />
                  <span className='ta-grid__pending-proposed'>
                    {describeValue(change, change.proposed, change.proposedWeekly)}
                  </span>
                </div>
              ))}
            </div>
          </td>
        )
      }

      if (state === 'notTaught') {
        return (
          <td key={cellKey} className={classes}>
            <button
              type='button'
              className='ta-grid__not-taught'
              disabled={readOnly}
              onClick={() => onSettings(row, subject)}
              aria-label={t('GRID.NOT_TAUGHT_ARIA', {subject, section})}
            >
              {t('GRID.NOT_TAUGHT')}
            </button>
          </td>
        )
      }

      const showTip = (target: HTMLElement) => {
        const rect = target.getBoundingClientRect()
        onTip({
          subject,
          section: row.label,
          branch: row.branch,
          x: rect.left + rect.width / 2,
          y: rect.top,
        })
      }
      return (
        <td
          key={cellKey}
          className={classes}
          onClick={() => {
            if (!readOnly) onSettings(row, subject)
          }}
        >
          <div className='ta-grid__cell-inner'>
            <AllocationTeacherSelect
              teachers={teachers}
              value={String(cell?.teacher ?? '')}
              selectedEmployeeId={String(cell?.teacherEmployeeId ?? '')}
              imported={Boolean(importedSet?.has(subject))}
              disabled={readOnly}
              ariaLabel={t('GRID.TEACHER_ARIA', {subject, section})}
              onSelect={(teacher) => onSelect(row, subject, teacher)}
            />
            <button
              type='button'
              className='ta-grid__gear'
              disabled={readOnly}
              aria-label={t('GRID.SETTINGS_ARIA', {subject, section})}
              onMouseEnter={(e) => showTip(e.currentTarget)}
              onFocus={(e) => showTip(e.currentTarget)}
              onMouseLeave={() => onTip(null)}
              onBlur={() => onTip(null)}
              onClick={(e) => {
                e.stopPropagation()
                onTip(null)
                onSettings(row, subject)
              }}
            >
              <Settings size={17} aria-hidden />
            </button>
          </div>
        </td>
      )
    }

    return (
      <tr ref={measureRef} data-index={dataIndex}>
        <th scope='row' className='ta-grid__row-head'>
          <div className='ta-grid__row-head-inner'>
            <span title={row.label}>{row.label}</span>
            {/* Handover 3C: the whole-section removal cross shows only under All. */}
            {statusFilter === 'all' && (
              <button
                type='button'
                className='ta-grid__remove'
                disabled={readOnly}
                aria-label={t('GRID.REMOVE_ROW_ARIA', {section})}
                onClick={() => onRemoveRow(row)}
              >
                <X size={16} aria-hidden />
              </button>
            )}
          </div>
        </th>
        {columns.map(renderCell)}
      </tr>
    )
  }
)
GridRow.displayName = 'GridRow'

/**
 * Viewport assumed until the scroll box has a measured height (first paint before layout, and
 * jsdom, where nothing has a size): renders a screenful of rows instead of none.
 */
const FALLBACK_RECT = {width: 1200, height: 720}

type BodyItem = {type: 'group'; branch: string} | {type: 'row'; row: AllocationRow}

type BodyProps = Omit<
  RowProps,
  | 'row'
  | 'cells'
  | 'notTaught'
  | 'imported'
  | 'flashing'
  | 'flashTone'
  | 'flashKey'
  | 'activeSubject'
  | 'section'
  | 'dataIndex'
  | 'measureRef'
> & {
  flash?: AllocationGridFlash | null
  items: BodyItem[]
  grid: LessonPlanningData
  deleted: ReadonlySet<string>
  importedSlots: ReadonlySet<string>
  activeSlotKey: string | null
  withBranch: boolean
  scrollRef: RefObject<HTMLDivElement | null>
}

const subjectsWhere = (row: AllocationRow, set: ReadonlySet<string>) =>
  row.subjects.filter((subject) => set.has(slotKeyFor(row, subject))).join('\n')

/**
 * Only the rows near the viewport are rendered, plus two spacer rows that keep the scroll height
 * (the pattern of common/components/grid/VirtualizedRows): a school with hundreds of sections
 * costs the same to open, filter and edit as one with a dozen.
 */
const GridBody: FC<BodyProps> = ({
  items,
  grid,
  deleted,
  importedSlots,
  activeSlotKey,
  withBranch,
  scrollRef,
  columns,
  flash,
  ...rowProps
}) => {
  // The virtualizer mutates internally on scroll; compiler-cached results would freeze the window.
  'use no memo'

  const virtualizer = useVirtualizer({
    count: items.length,
    getScrollElement: () => scrollRef.current,
    estimateSize: (index) => (items[index]?.type === 'group' ? 46 : 58),
    // A row with no measured height (hidden, or jsdom) keeps its estimate instead of collapsing
    // to 0, which would make every row count as visible.
    measureElement: (element, entry, instance) => {
      const size = measureElement(element, entry, instance)
      if (size > 0) return size
      const index = Number((element as HTMLElement).dataset.index)
      return items[index]?.type === 'group' ? 46 : 58
    },
    overscan: 8,
    initialRect: FALLBACK_RECT,
    observeElementRect: (instance, cb) =>
      observeElementRect(instance, (rect) => cb(rect.height > 0 ? rect : FALLBACK_RECT)),
    getItemKey: (index) => {
      const item = items[index]
      return item.type === 'group' ? `group:${item.branch}` : item.row.classKey
    },
  })
  // The virtualizer subscribes to the scroll container in a layout effect, but on mount that runs
  // before React attaches the container's ref (an ancestor's ref attaches after its children's
  // layout effects). It then sees no container, and unless something else re-renders this body
  // it never follows the scroll: only the first window of rows ever renders and the rest of the
  // grid scrolls as blank space. One render after mount, once the ref is set, subscribes it.
  const [, rerenderAfterMount] = useReducer((n: number) => n + 1, 0)
  useEffect(() => {
    rerenderAfterMount()
  }, [])

  const virtualItems = virtualizer.getVirtualItems()
  const totalSize = virtualizer.getTotalSize()
  const paddingTop = virtualItems.length > 0 ? virtualItems[0].start : 0
  const paddingBottom =
    virtualItems.length > 0 ? totalSize - virtualItems[virtualItems.length - 1].end : 0
  const colSpan = columns.length + 1

  return (
    <tbody>
      {paddingTop > 0 && (
        <tr aria-hidden='true' style={{height: paddingTop}}>
          <td colSpan={colSpan} style={{padding: 0, border: 0}} />
        </tr>
      )}
      {virtualItems.map((virtualItem) => {
        const item = items[virtualItem.index]
        if (item.type === 'group') {
          return (
            <tr
              key={virtualItem.key}
              className='ta-grid__group'
              ref={virtualizer.measureElement}
              data-index={virtualItem.index}
            >
              <th scope='rowgroup' colSpan={colSpan}>
                <span>{item.branch}</span>
              </th>
            </tr>
          )
        }
        const {row} = item
        const activeSubject =
          activeSlotKey && activeSlotKey.startsWith(`${row.classKey}|||`)
            ? activeSlotKey.slice(row.classKey.length + 3)
            : null
        return (
          <GridRow
            key={virtualItem.key}
            {...rowProps}
            row={row}
            columns={columns}
            cells={grid[row.classKey]}
            notTaught={subjectsWhere(row, deleted)}
            imported={subjectsWhere(row, importedSlots)}
            flashing={flash ? subjectsWhere(row, flash.slots) : undefined}
            flashTone={flash?.tone}
            flashKey={flash?.key}
            activeSubject={activeSubject}
            section={withBranch ? `${row.label} (${row.branch})` : row.label}
            dataIndex={virtualItem.index}
            measureRef={virtualizer.measureElement}
          />
        )
      })}
      {paddingBottom > 0 && (
        <tr aria-hidden='true' style={{height: paddingBottom}}>
          <td colSpan={colSpan} style={{padding: 0, border: 0}} />
        </tr>
      )}
    </tbody>
  )
}

/**
 * The shared review and manual-entry grid (handover, Point 3): View allocation grid, Review
 * allocation grid and Assign manually → Open grid all land here with the same data and controls.
 * Rows are sections (grouped by branch under All branches), columns are subjects. Filters keep
 * the matrix aligned: nonmatching cells are left empty, wholly nonmatching rows/columns hidden.
 */
const AllocationGrid: FC<Props> = ({
  rows,
  grid,
  deleted,
  teachers,
  importedSlots,
  counts,
  hasImports,
  branch,
  level,
  onBranchChange,
  onLevelChange,
  levelLabel,
  defaultFrequency,
  activeSlotKey,
  preview,
  onSelectTeacher,
  onOpenSettings,
  onRemoveColumn,
  onRemoveRow,
  onOpenAi,
  footer,
  scopeOptions,
  loading,
  refreshing,
  changeAnimations,
  flash,
}) => {
  const intl = useIntl()
  const t = (id: string, values?: Record<string, ReactNode>) =>
    intl.formatMessage({id: `ONBOARDING_WIZARD.TEACHER_ALLOCATION.${id}`}, values as any)
  const readOnly = Boolean(preview)
  // Kept here, not in the step: switching the filter re-renders the grid only.
  const [statusChoice, setStatusChoice] = useState<AllocationStatusFilter>('all')
  // The preview shows every pending change, whatever the filter was.
  const statusFilter: AllocationStatusFilter = preview ? 'all' : statusChoice
  // One tooltip for every gear instead of one overlay per cell.
  const [tip, setTip] = useState<Tip | null>(null)

  const branches = scopeOptions?.branches ?? filterChoices(branchValues(rows))
  const levels = scopeOptions?.levels ?? filterChoices(levelValues(rows, branch))
  const serverScoped = Boolean(scopeOptions)
  const matrix = useMemo(
    () =>
      buildAllocationMatrix(
        serverScoped ? rows : rowsInScope(rows, branch, level),
        statusFilter,
        grid,
        deleted
      ),
    [rows, branch, level, statusFilter, grid, deleted, serverScoped]
  )
  const showGroups = scopeOptions ? scopeOptions.groupByBranch : branches.hasAll && branch === ''
  const items = useMemo<BodyItem[]>(
    () =>
      showGroups
        ? groupRowsByBranch(matrix.rows).flatMap((group) => [
            {type: 'group' as const, branch: group.branch},
            ...group.rows.map((row) => ({type: 'row' as const, row})),
          ])
        : matrix.rows.map((row) => ({type: 'row' as const, row})),
    [matrix.rows, showGroups]
  )
  const scrollRef = useRef<HTMLDivElement>(null)

  const handleSelect = useStableCallback(onSelectTeacher)
  const handleSettings = useStableCallback(onOpenSettings)
  const handleRemoveRow = useStableCallback(onRemoveRow)

  return (
    <div className='ta-page ta-grid-page'>
      <header className='ta-page__header'>
        <h1 className='ta-page__title'>{hasImports ? t('GRID.TITLE_REVIEW') : t('GRID.TITLE')}</h1>
        <p className='ta-page__subtitle'>
          {hasImports ? t('GRID.SUBTITLE_IMPORTED') : t('GRID.SUBTITLE')}
        </p>
      </header>

      <section className='ta-card ta-grid-page__progress' aria-live='polite'>
        <div className='ta-grid-page__progress-text'>
          <span className='ta-grid-page__progress-caption'>{t('PROGRESS.OVERALL')}</span>
          <strong>
            {t('PROGRESS.ASSIGNMENTS_FILLED', {filled: counts.filled, total: counts.total})}
          </strong>
          <span className='ta-grid-page__progress-caption'>
            {t('PROGRESS.REMAINING', {count: counts.remaining})}
          </span>
        </div>
        <AllocationProgressBar counts={counts} label={t('PROGRESS.ARIA')} />
        <p className='ta-grid-page__autosave'>
          <FileCheck2 size={22} aria-hidden />
          {hasImports ? t('GRID.AUTOSAVE_IMPORTED') : t('GRID.AUTOSAVE')}
        </p>
      </section>

      {preview && (
        <section className='ta-card ta-grid-page__preview' role='status'>
          <div>
            <strong>{t('GRID.PREVIEW_TITLE')}</strong>
            <span className='ta-badge ta-badge--warning'>{t('AI.NOT_APPLIED')}</span>
            <p>{t('GRID.PREVIEW_TEXT', {count: preview.count})}</p>
            {Boolean(preview.outside) && (
              <p>{t('GRID.PREVIEW_OUTSIDE', {count: preview.outside})}</p>
            )}
          </div>
          <div className='ta-grid-page__preview-actions'>
            <button
              type='button'
              className='ta-btn ta-btn--primary'
              onClick={preview.onApply}
              disabled={preview.isApplying}
            >
              {preview.isApplying ? (
                <span className='spinner-border spinner-border-sm' aria-hidden />
              ) : (
                <Check size={16} aria-hidden />
              )}
              {t('GRID.PREVIEW_APPLY')}
            </button>
            <button
              type='button'
              className='ta-btn ta-btn--outline'
              onClick={preview.onBackToConversation}
            >
              {t('GRID.PREVIEW_BACK')}
            </button>
            <button type='button' className='ta-btn ta-btn--ghost' onClick={preview.onDiscard}>
              {t('GRID.PREVIEW_DISCARD')}
            </button>
          </div>
        </section>
      )}

      <div className='ta-grid-page__toolbar'>
        <div className='ta-segmented' role='tablist' aria-label={t('GRID.STATUS_FILTER')}>
          {STATUS_FILTERS.map((filter) => (
            <button
              key={filter}
              type='button'
              role='tab'
              aria-selected={statusFilter === filter}
              className={statusFilter === filter ? 'is-active' : ''}
              disabled={readOnly}
              onClick={() => setStatusChoice(filter)}
            >
              {t(`GRID.FILTER_${filter.toUpperCase()}`)}
            </button>
          ))}
        </div>
        <div className='ta-grid-page__filters'>
          <AllocationFilterSelect
            label={t('FILTER.BRANCH')}
            value={branch}
            values={branches.values}
            hasAll={branches.hasAll}
            allLabel={t('FILTER.ALL')}
            optionLabel={scopeOptions?.branchLabel}
            // Server flow: one branch is shown locked (§3.1).
            disabled={Boolean(scopeOptions) && !branches.hasAll && branches.values.length <= 1}
            onChange={onBranchChange}
          />
          <AllocationFilterSelect
            label={t('FILTER.LEVEL')}
            value={level}
            values={levels.values}
            hasAll={levels.hasAll}
            allLabel={t('FILTER.ALL')}
            optionLabel={levelLabel}
            disabled={Boolean(scopeOptions) && !levels.hasAll && levels.values.length <= 1}
            onChange={onLevelChange}
          />
          <button
            type='button'
            className='ta-btn ta-btn--outline'
            onClick={onOpenAi}
            disabled={readOnly}
          >
            <Sparkles size={18} aria-hidden />
            {t('GRID.ASSIGN_WITH_AI')}
          </button>
        </div>
      </div>

      <p className='ta-grid-page__helper'>{t('GRID.HELPER')}</p>

      {refreshing && !loading && (
        <div className='ta-grid-refresh' role='status' aria-label={t('LOADING')} />
      )}

      {loading ? (
        <GridSkeleton columns={matrix.columns.length || 5} />
      ) : matrix.rows.length === 0 || matrix.columns.length === 0 ? (
        <div className='ta-card ta-grid-page__empty'>
          {rows.length === 0 ? t('GRID.EMPTY_NO_CLASSES') : t('GRID.EMPTY_FILTER')}
        </div>
      ) : (
        <div
          ref={scrollRef}
          className='ta-grid'
          role='region'
          aria-label={t('GRID.REGION')}
          tabIndex={0}
          onScroll={() => setTip(null)}
        >
          {/* Fixed layout: columns keep their width as rows mount and unmount while scrolling. */}
          <table style={{width: 170 + matrix.columns.length * 210}}>
            <thead>
              <tr>
                <th scope='col' className='ta-grid__corner'>
                  {t('GRID.CLASS_SECTION')}
                </th>
                {matrix.columns.map((subject) => (
                  <th key={subject} scope='col' className='ta-grid__col-head'>
                    <span className='ta-grid__col-name' title={subject}>
                      {subject}
                    </span>
                    <button
                      type='button'
                      className='ta-grid__remove'
                      disabled={readOnly}
                      aria-label={t('GRID.REMOVE_COLUMN_ARIA', {subject})}
                      onClick={() => onRemoveColumn(subject, statusFilter)}
                    >
                      <X size={16} aria-hidden />
                    </button>
                  </th>
                ))}
              </tr>
            </thead>
            <GridBody
              items={items}
              grid={grid}
              deleted={deleted}
              importedSlots={importedSlots}
              activeSlotKey={activeSlotKey}
              withBranch={branches.hasAll}
              scrollRef={scrollRef}
              columns={matrix.columns}
              statusFilter={statusFilter}
              preview={preview}
              teachers={teachers}
              changeAnimations={changeAnimations}
              flash={flash}
              defaultFrequency={defaultFrequency}
              onSelect={handleSelect}
              onSettings={handleSettings}
              onRemoveRow={handleRemoveRow}
              onTip={setTip}
            />
          </table>
        </div>
      )}

      {tip && (
        <div className='ta-floating-tip' role='tooltip' style={{left: tip.x, top: tip.y}}>
          {t('GRID.SETTINGS_TOOLTIP', {subject: tip.subject})}
          <br />
          {tip.section} · {tip.branch}
        </div>
      )}

      {footer}
    </div>
  )
}

export default AllocationGrid
